// Group sign-off dialog for a consolidated shop-floor row.
//
// The operator enters ONE total quantity (e.g. "10" across two WOs of 5);
// the dialog splits it earliest-due-first across the group's source work
// orders — and each row's share is EDITABLE, so the operator can decide
// exactly how many units go to which work order before signing off.
// Typing in the total re-runs the auto-split (overwriting manual edits);
// editing a row updates that row and the total becomes the sum.
// Every source row shows exactly which customer order it belongs to, so
// the operator always knows where the units are going.
//
// Scrap is entered ONCE for the whole group, not per order. Scrap is EXTRA
// to the good count ("made 6, scrapped 2" = 8 attempts): it never counts
// towards a work order's remaining need. The whole scrap total is recorded
// against the most urgent work order that receives good units (or the most
// urgent eligible one when nothing good was made), so the replacement is
// planned on the order that needs it soonest. The server plans and checks
// the replacement material, so only the good units are pre-flighted here.
//
// "Scrapped parts" are different: a component of the unit (a table top, a
// leg frame) ruined and remade on the spot — NOT a whole unit. Each one is
// posted to the WO scrap endpoint after the sign-off, against the most
// urgent work order that received good units: its raw material comes out
// of stock, no work order is touched (services/scrap.py). They can be
// recorded on their own, with nothing made.
//
// Material pre-flight runs for EVERY allocation before anything is written;
// one shortage aborts the whole batch (no half-signed groups).
//
// Each source's "can make" is fetched on open and the auto-split fills WOs
// only up to that first. Un-issued production is earmarked per order tree,
// so a group can have e.g. 4 makeable on one order's WO and 0 on another's —
// an earliest-due split that ignored this sent units to the WO that couldn't
// make them and the pre-flight then blocked the whole sign-off.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, MenuItem, Stack, TextField,
  Typography,
} from '@mui/material';
import { Close, Factory, PlayArrow, SubdirectoryArrowRight } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { v2 } from '../../../../services/api';
import {
  allocateGroupQuantity, compareByDueThenNumber,
} from '../utils/allocateGroupQuantity';
import { orderStatusColor, orderStatusLabel } from '../utils/orderStatus';
import MaterialShortageDialogV2 from './MaterialShortageDialogV2';

export default function GroupProductionDialogV2({
  open, onClose, sources = [], onDone, groupLabel = '',
}) {
  const [qty, setQty] = useState('');
  // Per-WO editable share: step_id -> string. Seeded by the auto-split,
  // then the operator can redirect units between work orders.
  const [rowQty, setRowQty] = useState({});
  // Scrapped units for the whole group. Extra to the good count.
  const [scrap, setScrap] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shortage, setShortage] = useState(null); // {woNumber, shortages}
  // step_id -> units that WO can make now (null = unknown, uncapped).
  const [capacity, setCapacity] = useState(null);
  const [capacityLoading, setCapacityLoading] = useState(false);
  // Components of the unit (sub-assemblies + parts) for the "Scrapped
  // parts" picker, fetched from the first eligible WO — every WO in the
  // group makes the same item so the list is the same for all of them.
  const [components, setComponents] = useState([]);
  const [scrappedParts, setScrappedParts] = useState([]);
  // "What was scrapped?" dropdown: 'whole' (finished units) or 'parts'
  // (line-by-line specific parts). One explicit choice, never both.
  const [scrapMode, setScrapMode] = useState('whole');

  const isEligible = (s) => s && (s.status === 'planned' || s.status === 'in_progress');
  const remainingOf = (s) => {
    const total = parseFloat(s.quantity_to_produce) || 0;
    const done = parseFloat(s.quantity_completed) || 0;
    return Math.max(0, total - done);
  };

  const numericQty = parseFloat(qty) || 0;
  const totalScrap = parseFloat(scrap) || 0;
  const scrapNegative = totalScrap < 0;

  // List rows in the exact order the auto-split fills them (earliest due
  // first), so entering a total visibly fills row 1, then row 2, then
  // row 3 — never a scatter.
  const orderedSources = useMemo(
    () => [...sources].sort(compareByDueThenNumber),
    [sources],
  );

  const groupRemaining = useMemo(
    () => sources.reduce((t, s) => (isEligible(s) ? t + remainingOf(s) : t), 0),
    [sources],
  );

  const applyAutoSplit = (total, cap = capacity) => {
    const { allocations } = allocateGroupQuantity(sources, total, cap);
    const next = {};
    sources.forEach((s) => {
      const a = allocations.find((x) => x.step.step_id === s.step_id);
      next[s.step_id] = String(a ? a.quantity : 0);
    });
    setRowQty(next);
  };

  // Prefill with the group's full remaining need — most sign-offs are "we
  // made the lot", so the operator usually just hits the button.
  // Once capacity is known, prefill with what can actually be made instead.
  useEffect(() => {
    if (!open) return undefined;
    setQty(groupRemaining > 0 ? String(groupRemaining) : '');
    setCapacity(null);
    applyAutoSplit(groupRemaining, null);

    let cancelled = false;
    const eligible = sources.filter((s) => isEligible(s) && remainingOf(s) > 0);
    setCapacityLoading(eligible.length > 0);

    setScrappedParts([]);
    setScrapMode('whole');
    setComponents([]);
    const partSource = [...sources].sort(compareByDueThenNumber).find(isEligible);
    if (partSource && v2.workOrders.materialRequirements) {
      v2.workOrders.materialRequirements(
        partSource.work_order_id, { step: partSource.step_id },
      ).then((res) => {
        if (cancelled) return;
        setComponents((res.data?.requirements || []).map((r) => ({
          item_id: r.item_id,
          item_code: r.item_code,
          item_name: r.item_name,
          per_unit: Math.round((parseFloat(r.required) || 0) * 1000) / 1000,
        })));
      }).catch((err) => {
        if (!cancelled) console.error('material-requirements prefetch failed', err);
      });
    }
    Promise.all(eligible.map(async (s) => {
      try {
        const res = await v2.workOrders.materialAvailability(
          s.work_order_id, { quantity: remainingOf(s), step: s.step_id },
        );
        // parseFloat, not parseInt — see MaterialAwareStartButtonV2.
        let max = Math.floor(parseFloat(res.data?.max_producible));
        if (Number.isNaN(max)) return [s.step_id, null];
        // A later step can't outrun what its predecessor has released.
        if (s.depends_on_id != null && s.qty_available_from_previous_step != null) {
          const prev = parseFloat(s.qty_available_from_previous_step);
          if (!Number.isNaN(prev)) max = Math.min(max, Math.floor(prev));
        }
        return [s.step_id, Math.max(0, max)];
      } catch (err) {
        return [s.step_id, null];
      }
    })).then((pairs) => {
      if (cancelled) return;
      const cap = Object.fromEntries(pairs);
      setCapacity(cap);
      const makeable = eligible.reduce((t, s) => {
        const c = cap[s.step_id];
        return t + (c == null ? remainingOf(s) : Math.min(remainingOf(s), c));
      }, 0);
      const prefill = makeable > 0 ? makeable : groupRemaining;
      setQty(prefill > 0 ? String(prefill) : '');
      applyAutoSplit(prefill, cap);
    }).finally(() => {
      if (!cancelled) setCapacityLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, groupRemaining]);

  const handleTotalChange = (v) => {
    setQty(v);
    applyAutoSplit(parseFloat(v) || 0);
  };

  const handleRowChange = (s, v) => {
    const next = { ...rowQty, [s.step_id]: v };
    setRowQty(next);
    const sum = sources.reduce(
      (t, src) => (isEligible(src) ? t + (parseFloat(next[src.step_id]) || 0) : t),
      0,
    );
    setQty(String(sum));
  };

  // The rows ARE the allocation — the total is derived from them. The
  // group's scrap rides on the first (most urgent) row with good units, or
  // the most urgent eligible row when nothing good was made.
  const allocations = useMemo(() => {
    const rows = orderedSources
      .filter(isEligible)
      .map((s) => ({ step: s, quantity: parseFloat(rowQty[s.step_id]) || 0, scrap: 0 }));
    if (totalScrap > 0 && rows.length > 0) {
      const carrier = rows.find((r) => r.quantity > 0) || rows[0];
      carrier.scrap = totalScrap;
    }
    return rows.filter((a) => a.quantity > 0 || a.scrap > 0);
  }, [orderedSources, rowQty, totalScrap]);
  const totalAllocated = allocations.reduce((t, a) => t + a.quantity, 0);
  const anyRowOver = sources.some(
    (s) => isEligible(s) && (parseFloat(rowQty[s.step_id]) || 0) > remainingOf(s),
  );
  const anyRowNegative = sources.some(
    (s) => isEligible(s) && (parseFloat(rowQty[s.step_id]) || 0) < 0,
  );
  // Total typed in exceeds what the group can absorb.
  const leftover = Math.max(0, numericQty - totalAllocated);

  const addPart = () => {
    const first = components.find((c) => !scrappedParts.some((p) => p.item_id === c.item_id))
      || components[0];
    if (!first) return;
    setScrappedParts((prev) => [
      ...prev,
      { key: Date.now() + prev.length, item_id: first.item_id, quantity: String(first.per_unit || 1), reason: '' },
    ]);
  };
  // Switching modes clears the other one's entry, and the parts list starts
  // off with one line ready to fill in rather than an empty list.
  const changeScrapMode = (mode, itemId = null) => {
    setScrapMode(mode);
    if (mode === 'parts') {
      setScrap('');
      const first = components.find((c) => c.item_id === itemId) || components[0];
      if (first) {
        setScrappedParts((prev) => (prev.length ? prev : [
          { key: Date.now(), item_id: first.item_id, quantity: String(first.per_unit || 1), reason: '' },
        ]));
      }
    } else {
      setScrappedParts([]);
    }
  };
  const updatePart = (key, patch) => setScrappedParts((prev) => prev.map((p) => {
    if (p.key !== key) return p;
    const next = { ...p, ...patch };
    if (patch.item_id != null && patch.item_id !== p.item_id) {
      const comp = components.find((c) => c.item_id === patch.item_id);
      if (comp) next.quantity = String(comp.per_unit || 1);
    }
    return next;
  }));
  const removePart = (key) => setScrappedParts((prev) => prev.filter((p) => p.key !== key));
  const validParts = scrappedParts.filter((p) => p.item_id && (parseFloat(p.quantity) || 0) > 0);
  const partsOnly = totalAllocated <= 0 && totalScrap <= 0 && validParts.length > 0;
  // Scrapped parts go on the most urgent WO that gets good units, else the
  // most urgent eligible one — same rule as the group scrap total.
  const partsCarrier = allocations.find((a) => a.quantity > 0)?.step
    || orderedSources.find(isEligible) || null;

  const canSubmit = !submitting && !capacityLoading
    && (totalAllocated > 0 || totalScrap > 0 || validParts.length > 0)
    && !anyRowOver && !anyRowNegative && !scrapNegative && leftover <= 0
    && (!partsOnly || partsCarrier);

  const handleClose = () => {
    if (submitting) return;
    setQty('');
    setRowQty({});
    setScrap('');
    setScrappedParts([]);
    setShortage(null);
    onClose();
  };

  // Post the scrapped parts via the WO scrap endpoint (remake: material out
  // of stock, WO untouched). Runs AFTER the sign-off so a blocked sign-off
  // records nothing.
  const recordScrappedParts = async (carrier) => {
    if (!carrier || validParts.length === 0) return;
    const done = [];
    for (const part of validParts) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await v2.workOrders.scrap(carrier.work_order_id, {
          item: part.item_id,
          quantity: String(parseFloat(part.quantity)),
          step: carrier.step_id,
          notes: (part.reason || '').trim() || `Scrapped at ${carrier.wo_number} sign-off`,
        });
        done.push(`${parseFloat(part.quantity)} × ${res.data?.item_code || part.item_id}`);
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.error || 'Failed to record a scrapped part');
      }
    }
    if (done.length) {
      toast.success(`Scrapped parts recorded on ${carrier.wo_number}: ${done.join(', ')} — material taken from stock`);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Phase 1 — pre-flight every allocation before writing anything.
      // Good units only: the server plans and checks the scrap's material.
      for (const a of allocations) {
        if (a.quantity <= 0) continue;
        const res = await v2.workOrders.materialAvailability(
          a.step.work_order_id,
          { quantity: a.quantity, step: a.step.step_id },
        );
        if (res.data?.shortages?.length > 0) {
          setShortage({
            woNumber: a.step.wo_number,
            shortages: res.data.shortages,
          });
          return;
        }
      }

      // Phase 2 — sign each one off. Complete starts a planned step itself,
      // issuing materials only for this WO's share (partial sign-off).
      for (const a of allocations) {
        await v2.workOrders.completeStep(
          a.step.work_order_id,
          a.step.step_id,
          a.scrap > 0
            ? { quantity_good: a.quantity, quantity_scrapped: a.scrap }
            : { quantity_good: a.quantity },
        );
      }

      if (allocations.length > 0) {
        toast.success(
          `Signed off ${totalAllocated} unit${totalAllocated === 1 ? '' : 's'} across `
          + `${allocations.length} work order${allocations.length === 1 ? '' : 's'}`
          + (totalScrap > 0 ? ` (${totalScrap} scrapped)` : ''),
        );
      }
      await recordScrappedParts(partsCarrier);
      setQty('');
      setRowQty({});
      setScrap('');
      setScrappedParts([]);
      if (onDone) onDone();
      onClose();
    } catch (err) {
      console.error('Group production failed', err);
      toast.error(
        err?.response?.data?.detail
        || err?.response?.data?.error
        || 'Failed to record production — check the work orders before retrying',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="group-production-dialog">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Factory /> Record Production — {groupLabel || 'combined work orders'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the total made — it fills the most urgent work order first —
            or edit each work order&apos;s share below to choose exactly where
            the units go. Every unit is signed to a specific customer order.
          </Typography>

          {capacityLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Checking what each work order can make…
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <TextField
              label={`Quantity made (group needs ${groupRemaining})`}
              type="number"
              size="medium"
              fullWidth
              autoFocus
              value={qty}
              onChange={(e) => handleTotalChange(e.target.value)}
              inputProps={{ 'data-testid': 'group-qty-input', min: 0 }}
            />
          </Box>

          {leftover > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="group-leftover-warning">
              That&apos;s {leftover} more than these work orders need. Lower the
              quantity — overproduction must be recorded on an individual work
              order, not a group.
            </Alert>
          )}

          <Divider sx={{ mb: 1.5 }} />
          {/* Scrap — two clearly separate choices:
              1. whole finished units written off (extra to the good count,
                 replacements planned on the most urgent WO), or
              2. specific parts of a unit (the top, not the table) remade on
                 the spot — a dropdown opens the line-by-line list. Posted
                 via the WO scrap endpoint after sign-off. */}
          <Box
            data-testid="group-scrapped-parts"
            sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <Typography variant="subtitle2" gutterBottom>Scrap</Typography>
            {scrapMode === 'whole' && (
              <>
                <TextField
                  label="Whole finished units scrapped"
                  type="number"
                  size="small"
                  fullWidth
                  value={scrap}
                  onChange={(e) => setScrap(e.target.value)}
                  error={scrapNegative}
                  helperText={scrapNegative
                    ? 'cannot be negative'
                    : 'Complete units written off, on top of the quantity made — total for the group. Replacements still get made.'}
                  inputProps={{ 'data-testid': 'group-scrap-input', min: 0 }}
                  sx={{ mb: 1.5 }}
                />
                {/* Picking a part here swaps the card to line-by-line mode
                    with that part as the first line. */}
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Or scrap a specific part of a unit instead…"
                  value=""
                  onChange={(e) => changeScrapMode('parts', Number(e.target.value))}
                  disabled={components.length === 0}
                  SelectProps={{ 'data-testid': 'group-scrap-mode', displayEmpty: true }}
                  helperText={components.length === 0
                    ? 'This step has no components to scrap.'
                    : 'A top, a frame… — the raw material comes out of stock and the unit is not scrapped.'}
                >
                  {components.map((c) => (
                    <MenuItem key={c.item_id} value={String(c.item_id)} data-testid={`group-scrap-part-option-${c.item_id}`}>
                      {c.item_code} — {c.item_name}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
            {scrapMode === 'parts' && (
              <Box data-testid="group-scrapped-parts-list">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Specific parts of a unit, ruined and made again — not a whole unit.
                    Raw material comes out of stock; the work orders are untouched.
                    {partsCarrier ? ` Recorded on ${partsCarrier.wo_number}.` : ''}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => changeScrapMode('whole')}
                    data-testid="group-scrap-mode-whole"
                    sx={{ flexShrink: 0, ml: 1, textTransform: 'none' }}
                  >
                    Scrap whole units instead
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={addPart}
                    disabled={components.length === 0}
                    data-testid="group-add-scrapped-part"
                    sx={{ flexShrink: 0, ml: 1 }}
                  >
                    + Add a part
                  </Button>
                </Box>
                {scrappedParts.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {components.length === 0 ? 'No components to scrap.' : 'None added yet.'}
                  </Typography>
                ) : (
              <Stack spacing={1}>
                {scrappedParts.map((part) => (
                  <Box
                    key={part.key}
                    sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}
                    data-testid={`group-scrapped-part-${part.key}`}
                  >
                    <TextField
                      select
                      size="small"
                      label="Part"
                      value={String(part.item_id)}
                      onChange={(e) => updatePart(part.key, { item_id: Number(e.target.value) })}
                      sx={{ flex: 2, minWidth: 200 }}
                      inputProps={{ 'data-testid': `group-scrapped-part-item-${part.key}` }}
                    >
                      {components.map((c) => (
                        <MenuItem key={c.item_id} value={String(c.item_id)}>
                          {c.item_code} — {c.item_name} ({c.per_unit} per unit)
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Qty"
                      type="number"
                      value={part.quantity}
                      onChange={(e) => updatePart(part.key, { quantity: e.target.value })}
                      inputProps={{ min: 0, step: 'any', 'data-testid': `group-scrapped-part-qty-${part.key}` }}
                      sx={{ width: 100 }}
                    />
                    <TextField
                      size="small"
                      label="Reason"
                      value={part.reason}
                      onChange={(e) => updatePart(part.key, { reason: e.target.value })}
                      inputProps={{ 'data-testid': `group-scrapped-part-reason-${part.key}` }}
                      sx={{ flex: 2, minWidth: 160 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removePart(part.key)}
                      data-testid={`group-remove-scrapped-part-${part.key}`}
                      aria-label="Remove scrapped part"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
                )}
              </Box>
            )}
          </Box>

          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            Where the units go
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Scrap is on top of the quantity made and doesn&apos;t count towards
            what a work order still needs — replacements still get made against
            the most urgent order.
          </Typography>
          <Stack spacing={1}>
            {orderedSources.map((s) => {
              const remaining = remainingOf(s);
              const eligible = isEligible(s);
              const rowVal = parseFloat(rowQty[s.step_id]) || 0;
              const rowOver = eligible && rowVal > remaining;
              const rowCap = capacity ? capacity[s.step_id] : null;
              const rowShort = eligible && rowCap != null && rowVal > rowCap;
              const highlighted = eligible && rowVal > 0;
              return (
                <Box
                  key={s.step_id}
                  data-testid={`group-split-${s.step_id}`}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                    p: 1, borderRadius: 1, border: '1px solid',
                    borderColor: rowOver ? 'error.main'
                      : highlighted ? 'primary.light' : 'divider',
                    bgcolor: highlighted ? 'primary.50' : 'transparent',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight="bold">{s.wo_number}</Typography>
                      {s.customer_order_number && (
                        <Chip
                          label={s.customer_order_number}
                          size="small"
                          color="primary"
                          sx={{ fontSize: '0.85rem', fontWeight: 700 }}
                        />
                      )}
                      <Typography variant="body2" fontWeight="bold">
                        {s.customer_name || ''}
                      </Typography>
                      {s.customer_order_status && (
                        <Chip
                          label={orderStatusLabel(s.customer_order_status)}
                          size="small"
                          color={orderStatusColor(s.customer_order_status)}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {eligible && rowCap != null && (
                        <Box
                          component="span"
                          data-testid={`group-row-canmake-${s.step_id}`}
                          sx={{ fontWeight: 700, color: rowCap >= remaining ? 'success.main' : 'warning.main' }}
                        >
                          can make {rowCap} ·{' '}
                        </Box>
                      )}
                      needs {remaining}
                      {s.due_date ? ` · due ${new Date(s.due_date).toLocaleDateString()}` : ''}
                    </Typography>
                    {/* Sub-assemblies that have to be made before this one. */}
                    {Array.isArray(s.prerequisites) && s.prerequisites.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {s.prerequisites.map((p) => (
                          <Box
                            key={p.work_order_id || p.wo_number}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
                          >
                            <SubdirectoryArrowRight sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              needs first: <strong>{p.output_item_name}</strong> ×{' '}
                              {parseFloat(p.quantity_to_produce) || 0}
                            </Typography>
                            <Chip
                              label={p.status}
                              size="small"
                              color={p.status === 'completed' ? 'success' : 'warning'}
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  {eligible ? (
                    <TextField
                      size="small"
                      type="number"
                      label="Make"
                      value={rowQty[s.step_id] ?? ''}
                      onChange={(e) => handleRowChange(s, e.target.value)}
                      error={rowOver || rowShort}
                      helperText={
                        rowOver ? `max ${remaining}`
                          : rowShort ? `only ${rowCap} makeable` : ' '
                      }
                      inputProps={{
                        min: 0,
                        'data-testid': `group-row-qty-${s.step_id}`,
                      }}
                      sx={{ width: 100 }}
                    />
                  ) : (
                    <Typography variant="h6" color="text.disabled" fontWeight="bold">
                      —
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={submitting ? <CircularProgress size={16} /> : <PlayArrow />}
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="group-production-submit"
          >
            {submitting ? 'Recording…' : partsOnly ? 'Record scrapped parts' : 'Sign off all'}
          </Button>
        </DialogActions>
      </Dialog>

      {shortage && (
        <MaterialShortageDialogV2
          open
          onClose={() => setShortage(null)}
          woNumber={shortage.woNumber}
          shortages={shortage.shortages}
          intro="Nothing was recorded. This work order's materials are short:"
        />
      )}
    </>
  );
}
